/* ============================================================================
   assets/nb-search.js — Nuttet Baby
   Predictive search for headerens søgefelt.

   Kilde: design_handoff_shopify/design/Header.dc.html (dropdown-markup +
   søgelogik nederst i <script data-dc-script>) og BEHAVIOR.md afsnit 4:
   debounce ~200 ms, dropdown med thumbnail + navn + kategori-tag + pris,
   tastaturnavigation (pil op/ned, Enter, Esc), tom-tilstand med link videre.

   Kontrakt mod sections/header.liquid — JS finder alt via data-attributter:
     [data-nb-search]                form-wrapperen (init-scope, én instans pr. form)
     [data-nb-search-input]          input[name="q"] (role="combobox")
     [data-nb-search-clear]          ryd-knap (skjules når feltet er tomt)
     [data-nb-search-results]        tom container (role="listbox"), bærer:
        data-predictive-url          routes.predictive_search_url
        data-search-url              routes.search_url
        data-all-url                 routes.all_products_collection_url
        data-money-format            shop.money_format  (valgfri, se fallback)
        data-i18n-searching          "Søger…"
        data-i18n-no-results         "Ingen resultater for “[query]”."
        data-i18n-no-results-link    linktekst under tom-tilstand
        data-i18n-all-results        sidste række: se alle resultater
        data-i18n-from-price         prefiks når prisen varierer
        data-i18n-tag-collection     tag på kollektions-rækker   (valgfri)
        data-i18n-tag-page           tag på side-rækker          (valgfri)
        data-i18n-results-count      "[count] resultater" til aria-live (valgfri)

   REGLER overholdt her:
     - INGEN brugervendt tekst i denne fil. Alt kommer fra data-attributterne
       ovenfor; mangler en attribut, udelades elementet i stedet for at falde
       tilbage på en hardkodet streng.
     - Ingen valutaberegning: beløb formateres med shop.money_format.
     - Vanilla JS, ingen afhængigheder, idempotent init (Section Rendering API).
   ============================================================================ */

(function () {
  'use strict';

  var DEBOUNCE_MS = 200; // BEHAVIOR.md afsnit 4
  var MIN_CHARS = 2; // under 2 tegn er forslagene ubrugelige — luk i stedet
  var API_LIMIT = 6; // resources[limit]
  var MAX_ROWS = 8; // maks. antal rækker i dropdownen (produkter først)
  var THUMB_PX = 84; // 42px CSS-boks * 2 for retina
  var CACHE_MAX = 20;

  /* ---------------------------------------------------------------- utils */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null && text !== '') node.textContent = text;
    return node;
  }

  function attr(node, name) {
    var value = node.getAttribute(name);
    return value == null ? '' : value;
  }

  /* Erstat en pladsholder uden at røre resten af strengen. */
  function fill(template, token, value) {
    if (!template) return '';
    return template.split(token).join(value);
  }

  /* Shopify CDN: bed om et thumbnail i stedet for originalen. */
  function sized(url, width) {
    if (!url) return '';
    var absolute = url.indexOf('//') === 0 ? 'https:' + url : url;
    return absolute + (absolute.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  /* -------------------------------------------------------- pengeformater */

  /*
    Predictive Search returnerer pris som streng. Afhængigt af API-version er
    det enten hele øre ("19900") eller major units med decimaler ("199,00").
    Vi normaliserer til øre og lader derefter shop.money_format bestemme
    tusindtalsseparator, decimaler og valutasuffiks.
  */
  function toCents(value) {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Math.round(value);

    var cleaned = String(value).replace(/[^0-9.,-]/g, '');
    if (!cleaned) return null;

    var lastSep = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));
    if (lastSep === -1) {
      var whole = parseInt(cleaned, 10);
      return isNaN(whole) ? null : whole;
    }

    var fraction = cleaned.slice(lastSep + 1);
    if (/^\d{1,2}$/.test(fraction)) {
      // Separatoren er en decimalseparator ("199,00" / "199.5").
      var integer = cleaned.slice(0, lastSep).replace(/[.,]/g, '') || '0';
      var amount = parseFloat(integer + '.' + fraction);
      return isNaN(amount) ? null : Math.round(amount * 100);
    }

    // Separatoren var tusindtalsseparator ("1.990" i øre).
    var digits = cleaned.replace(/[.,]/g, '');
    var asInt = parseInt(digits, 10);
    return isNaN(asInt) ? null : asInt;
  }

  function withDelimiters(cents, precision, thousands, decimal) {
    if (cents === null || isNaN(cents)) return '';
    var value = (cents / 100).toFixed(precision);
    var parts = value.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + (thousands || ''));
    var fraction = parts[1] ? (decimal || '.') + parts[1] : '';
    return whole + fraction;
  }

  /* Samme placeholder-sæt som Shopifys egen money-filter-dokumentation. */
  function formatMoney(cents, format) {
    if (cents === null || !format) return null;
    var plain = format.replace(/<[^>]*>/g, ''); // money_format kan indeholde markup
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = plain.match(placeholder);
    var value;

    switch (match && match[1]) {
      case 'amount':
        value = withDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = withDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = withDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = withDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = withDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount_with_apostrophe_separator':
        value = withDelimiters(cents, 2, "'", '.');
        break;
      default:
        return null;
    }

    return plain.replace(placeholder, value).trim();
  }

  /* Fallback hvis data-money-format mangler: Shopifys aktive valuta + Intl.
     Ingen egen valutaomregning — kun formatering af butikkens eget beløb. */
  function formatWithIntl(cents) {
    var shopify = window.Shopify;
    var currency = shopify && shopify.currency && shopify.currency.active;
    if (!currency || cents === null || typeof Intl === 'undefined') return null;
    try {
      var locale = document.documentElement.lang || undefined;
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency
      }).format(cents / 100);
    } catch (error) {
      return null;
    }
  }

  /* ------------------------------------------------------------ component */

  function NbSearch(form) {
    this.form = form;
    this.input = form.querySelector('[data-nb-search-input]');
    this.results = form.querySelector('[data-nb-search-results]');
    this.clearBtn = form.querySelector('[data-nb-search-clear]');
    if (!this.input || !this.results) return;

    this.moneyFormat = attr(this.results, 'data-money-format');
    this.endpoint = (attr(this.results, 'data-predictive-url') || '/search/suggest').replace(/\.json$/, '') + '.json';
    this.searchUrl = attr(this.results, 'data-search-url') || '/search';
    this.allUrl = attr(this.results, 'data-all-url') || '';

    this.listboxId = this.results.id || '';
    this.items = [];
    this.activeIndex = -1;
    this.isOpen = false;
    this.timer = null;
    this.controller = null;
    this.cache = new Map();
    this.lastTerm = '';

    // aria-live-region til skærmlæsere (tom hvis butikken ikke har teksten).
    this.status = el('span', 'nb-visually-hidden');
    this.status.setAttribute('role', 'status');
    this.status.setAttribute('aria-live', 'polite');
    this.form.appendChild(this.status);

    this.onInput = this.onInput.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onFocus = this.onFocus.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onDocClick = this.onDocClick.bind(this);
    this.onRowHover = this.onRowHover.bind(this);
    this.onClear = this.onClear.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    this.input.addEventListener('input', this.onInput);
    this.input.addEventListener('keydown', this.onKeydown);
    this.input.addEventListener('focus', this.onFocus);
    this.form.addEventListener('focusout', this.onFocusOut);
    this.form.addEventListener('submit', this.onSubmit);
    this.results.addEventListener('mousemove', this.onRowHover);
    document.addEventListener('click', this.onDocClick);
    if (this.clearBtn) this.clearBtn.addEventListener('click', this.onClear);

    this.input.setAttribute('aria-expanded', 'false');
    this.syncClear();
  }

  NbSearch.prototype.destroy = function () {
    if (!this.input) return;
    window.clearTimeout(this.timer);
    if (this.controller) this.controller.abort();
    this.input.removeEventListener('input', this.onInput);
    this.input.removeEventListener('keydown', this.onKeydown);
    this.input.removeEventListener('focus', this.onFocus);
    this.form.removeEventListener('focusout', this.onFocusOut);
    this.form.removeEventListener('submit', this.onSubmit);
    this.results.removeEventListener('mousemove', this.onRowHover);
    document.removeEventListener('click', this.onDocClick);
    if (this.clearBtn) this.clearBtn.removeEventListener('click', this.onClear);
  };

  /* ------------------------------------------------------------- handlers */

  NbSearch.prototype.onInput = function () {
    var self = this;
    this.syncClear();
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(function () {
      self.search(self.term());
    }, DEBOUNCE_MS);
  };

  NbSearch.prototype.onFocus = function () {
    if (this.term().length >= MIN_CHARS && this.items.length) this.open();
  };

  NbSearch.prototype.onFocusOut = function () {
    var self = this;
    window.setTimeout(function () {
      if (!self.form.contains(document.activeElement)) self.close();
    }, 0);
  };

  NbSearch.prototype.onDocClick = function (event) {
    if (!this.form.contains(event.target)) this.close();
  };

  NbSearch.prototype.onClear = function () {
    window.clearTimeout(this.timer);
    if (this.controller) this.controller.abort();
    this.input.value = '';
    this.items = [];
    this.lastTerm = '';
    this.results.textContent = '';
    this.status.textContent = '';
    this.close();
    this.syncClear();
    this.input.focus();
  };

  NbSearch.prototype.onSubmit = function () {
    // Native submit til routes.search_url — luk blot dropdownen.
    this.close();
  };

  NbSearch.prototype.onRowHover = function (event) {
    var row = event.target.closest ? event.target.closest('[data-nb-search-option]') : null;
    if (!row) return;
    var index = parseInt(row.getAttribute('data-nb-search-option'), 10);
    if (!isNaN(index) && index !== this.activeIndex) this.setActive(index);
  };

  NbSearch.prototype.onKeydown = function (event) {
    var key = event.key;

    if (key === 'Escape' || key === 'Esc') {
      if (this.isOpen) {
        event.preventDefault();
        this.close();
      }
      return;
    }

    if (key === 'Tab') {
      this.close();
      return;
    }

    if (key === 'ArrowDown' || key === 'Down') {
      if (!this.items.length) return;
      event.preventDefault();
      this.open();
      this.setActive(this.activeIndex + 1 >= this.items.length ? 0 : this.activeIndex + 1);
      return;
    }

    if (key === 'ArrowUp' || key === 'Up') {
      if (!this.items.length) return;
      event.preventDefault();
      this.open();
      this.setActive(this.activeIndex <= 0 ? this.items.length - 1 : this.activeIndex - 1);
      return;
    }

    if (key === 'Enter') {
      var active = this.rowAt(this.activeIndex);
      if (this.isOpen && active) {
        event.preventDefault();
        window.location.assign(active.href);
      } else {
        // Intet markeret → lad formularen gå til /search?q=
        this.close();
      }
    }
  };

  /* ---------------------------------------------------------------- state */

  NbSearch.prototype.term = function () {
    return (this.input.value || '').trim();
  };

  NbSearch.prototype.syncClear = function () {
    if (!this.clearBtn) return;
    this.clearBtn.hidden = (this.input.value || '').length === 0;
  };

  NbSearch.prototype.optionId = function (index) {
    return (this.listboxId || 'nb-search') + '-option-' + index;
  };

  NbSearch.prototype.rowAt = function (index) {
    if (index < 0) return null;
    return this.results.querySelector('[data-nb-search-option="' + index + '"]');
  };

  NbSearch.prototype.setActive = function (index) {
    var previous = this.rowAt(this.activeIndex);
    if (previous) {
      previous.classList.remove('is-active');
      previous.setAttribute('aria-selected', 'false');
    }

    this.activeIndex = index;
    var next = this.rowAt(index);
    if (!next) {
      this.input.removeAttribute('aria-activedescendant');
      return;
    }

    next.classList.add('is-active');
    next.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', next.id);
    if (next.scrollIntoView) next.scrollIntoView({ block: 'nearest' });
  };

  NbSearch.prototype.open = function () {
    if (this.isOpen) return;
    this.results.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.isOpen = true;
  };

  NbSearch.prototype.close = function () {
    if (!this.isOpen) return;
    this.results.hidden = true;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    var active = this.rowAt(this.activeIndex);
    if (active) {
      active.classList.remove('is-active');
      active.setAttribute('aria-selected', 'false');
    }
    this.activeIndex = -1;
    this.isOpen = false;
  };

  /* ---------------------------------------------------------------- fetch */

  NbSearch.prototype.search = function (term) {
    var self = this;

    if (term.length < MIN_CHARS) {
      if (this.controller) this.controller.abort();
      this.items = [];
      this.lastTerm = '';
      this.results.textContent = '';
      this.status.textContent = '';
      this.close();
      return;
    }

    if (term === this.lastTerm && this.items.length) {
      this.open();
      return;
    }

    if (this.cache.has(term)) {
      this.lastTerm = term;
      this.render(this.cache.get(term), term);
      return;
    }

    if (this.controller) this.controller.abort();
    this.controller = typeof AbortController !== 'undefined' ? new AbortController() : null;

    this.renderSearching();

    var url =
      this.endpoint +
      '?q=' + encodeURIComponent(term) +
      '&resources[type]=product,collection,page' +
      '&resources[limit]=' + API_LIMIT +
      '&resources[options][unavailable_products]=last';

    fetch(url, {
      signal: this.controller ? this.controller.signal : undefined,
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('predictive-search:' + response.status);
        return response.json();
      })
      .then(function (data) {
        var items = self.toItems(data, term);
        self.remember(term, items);
        if (self.term() !== term) return; // brugeren skrev videre imens
        self.lastTerm = term;
        self.render(items, term);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        // Netværksfejl: vis intet frem for en misvisende tom-tilstand.
        self.items = [];
        self.results.textContent = '';
        self.status.textContent = '';
        self.close();
      });
  };

  NbSearch.prototype.remember = function (term, items) {
    if (this.cache.size >= CACHE_MAX) {
      var oldest = this.cache.keys().next();
      if (!oldest.done) this.cache.delete(oldest.value);
    }
    this.cache.set(term, items);
  };

  /* Predictive Search-JSON → flad liste af rækker (produkter først). */
  NbSearch.prototype.toItems = function (data, term) {
    var results = (data && data.resources && data.resources.results) || {};
    var fromLabel = attr(this.results, 'data-i18n-from-price');
    var tagCollection = attr(this.results, 'data-i18n-tag-collection');
    var tagPage = attr(this.results, 'data-i18n-tag-page');
    var self = this;
    var items = [];

    (results.products || []).forEach(function (product) {
      if (!product || !product.url) return;
      items.push({
        url: product.url,
        title: product.title || '',
        image: self.imageOf(product),
        tag: product.type || '',
        price: self.priceOf(product, fromLabel)
      });
    });

    (results.collections || []).forEach(function (collection) {
      if (!collection || !collection.url) return;
      items.push({
        url: collection.url,
        title: collection.title || '',
        image: '',
        tag: tagCollection,
        price: ''
      });
    });

    (results.pages || []).forEach(function (page) {
      if (!page || !page.url) return;
      items.push({
        url: page.url,
        title: page.title || '',
        image: '',
        tag: tagPage,
        price: ''
      });
    });

    items = items.slice(0, MAX_ROWS);

    // Sidste række: "se alle resultater" — kun hvis teksten findes i DOM'en.
    var allResults = attr(this.results, 'data-i18n-all-results');
    if (items.length && allResults) {
      items.push({
        url: this.searchUrl + '?q=' + encodeURIComponent(term),
        title: allResults,
        image: '',
        tag: '',
        price: ''
      });
    }

    return items;
  };

  NbSearch.prototype.imageOf = function (product) {
    if (product.featured_image && product.featured_image.url) return product.featured_image.url;
    if (typeof product.image === 'string') return product.image;
    if (product.image && product.image.url) return product.image.url;
    return '';
  };

  NbSearch.prototype.priceOf = function (product, fromLabel) {
    var min = product.price_min != null ? product.price_min : product.price;
    var cents = toCents(min);
    if (cents === null) return '';

    var money = formatMoney(cents, this.moneyFormat);
    if (money === null) money = formatWithIntl(cents);
    if (money === null) return ''; // hellere ingen pris end en gættet valuta

    var varies =
      product.price_varies === true ||
      (product.price_min != null && product.price_max != null && product.price_min !== product.price_max);

    return varies && fromLabel ? fromLabel + ' ' + money : money;
  };

  /* --------------------------------------------------------------- render */

  NbSearch.prototype.renderSearching = function () {
    var text = attr(this.results, 'data-i18n-searching');
    if (!text || this.items.length) return; // undgå flimmer når der allerede vises noget
    this.results.textContent = '';
    this.results.appendChild(el('div', 'nb-search__empty', text));
    this.open();
  };

  NbSearch.prototype.render = function (items, term) {
    this.items = items;
    this.activeIndex = -1;
    this.input.removeAttribute('aria-activedescendant');
    this.results.textContent = '';

    if (items.length) {
      var list = el('div', 'nb-search__list');
      list.setAttribute('role', 'presentation');
      for (var i = 0; i < items.length; i++) list.appendChild(this.buildRow(items[i], i));
      this.results.appendChild(list);
      this.announce(fill(attr(this.results, 'data-i18n-results-count'), '[count]', String(items.length)));
    } else {
      this.results.appendChild(this.buildEmpty(term));
      this.announce(fill(attr(this.results, 'data-i18n-no-results'), '[query]', term));
    }

    this.open();
  };

  NbSearch.prototype.announce = function (text) {
    this.status.textContent = text || '';
  };

  NbSearch.prototype.buildRow = function (item, index) {
    var row = el('a', 'nb-search__row');
    row.href = item.url;
    row.id = this.optionId(index);
    row.tabIndex = -1;
    row.setAttribute('role', 'option');
    row.setAttribute('aria-selected', 'false');
    row.setAttribute('data-nb-search-option', String(index));

    var main = el('span', 'nb-search__rowmain');

    if (item.image) {
      var thumb = el('span', 'nb-search__thumb');
      var img = document.createElement('img');
      img.src = sized(item.image, THUMB_PX);
      img.srcset = sized(item.image, THUMB_PX) + ' 1x, ' + sized(item.image, THUMB_PX * 2) + ' 2x';
      img.width = 42;
      img.height = 42;
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      thumb.appendChild(img);
      main.appendChild(thumb);
    }

    var text = el('span', 'nb-search__text');
    text.style.minWidth = '0'; // så navnets ellipsis virker i flex-rækken
    text.appendChild(el('span', 'nb-search__name', item.title));
    if (item.price) text.appendChild(el('span', 'nb-search__price', item.price));
    main.appendChild(text);

    row.appendChild(main);
    if (item.tag) row.appendChild(el('span', 'nb-search__tag', item.tag));
    return row;
  };

  NbSearch.prototype.buildEmpty = function (term) {
    var box = el('div', 'nb-search__empty');
    var message = fill(attr(this.results, 'data-i18n-no-results'), '[query]', term);
    if (message) box.appendChild(document.createTextNode(message));

    var linkText = attr(this.results, 'data-i18n-no-results-link');
    if (linkText && this.allUrl) {
      box.appendChild(document.createElement('br'));
      var link = el('a', null, linkText);
      link.href = this.allUrl;
      box.appendChild(link);
    }
    return box;
  };

  /* ------------------------------------------------------------------ init */

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    var forms = root.querySelectorAll('[data-nb-search]');
    for (var i = 0; i < forms.length; i++) {
      var form = forms[i];
      if (form.nbSearch) continue; // idempotent: allerede initialiseret
      form.nbSearch = new NbSearch(form);
    }
  }

  // Samme mønster som nb-menu.js: ét navngivet objekt, ingen anden global state.
  window.NBSearch = { init: init };

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var forms = event.target.querySelectorAll ? event.target.querySelectorAll('[data-nb-search]') : [];
    for (var i = 0; i < forms.length; i++) {
      if (forms[i].nbSearch) {
        forms[i].nbSearch.destroy();
        forms[i].nbSearch = null;
      }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }
})();
